/**
 * NC-DRAW-02 (FIELD-CREWS-01 phase 0b, Seth 2026-09-24: "Each builder gets a
 * default draw split (e.g. 50/50), with the builder's PO printed on every
 * draw. Any house can be changed by hand.").
 *
 * A draw is a REAL invoice on the builder's account (bill_nc_draw): its own
 * number (SH-800001+ in the parallel era), the builder's PO, one line, status
 * issued — so it shows on the builder's 360 balance, the portal pay link,
 * Today's Invoices To Send and the invoice page, and payments allocate to it.
 *
 * Competitor frame: ServiceTitan / Jobber / Housecall Pro bill progress
 * invoices from the office by % of the contract, capped at 100%; builders
 * (Buildertrend, Hyphen) send HVAC subs a PO per house. Adopted: office-only
 * draws, a per-builder split, the cap (confirm to go over), the PO on every
 * draw. Skipped: retainage / AIA pay applications (commercial overhead).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { Job, PermitBy } from "./jobs";

export interface DrawStage { label: string; pct: number }
export interface Builder {
  custno: string;
  draw_split: DrawStage[] | null;
  permit_by: PermitBy | null;
  po_required: boolean;
  billing_email: string | null;
  notes: string | null;
  updated_at: string;
  updated_by: string | null;
}

export const NC_DRAW_SPLIT_FALLBACK: DrawStage[] = [
  { label: "Rough-In", pct: 50 },
  { label: "Final", pct: 50 },
];
/** Stage names offered for an extra (ad-hoc) draw. */
export const DRAW_STAGE_NAMES = ["Deposit", "Rough-In", "Trim", "Progress", "Final"];

function cleanSplit(v: unknown): DrawStage[] | null {
  if (!Array.isArray(v)) return null;
  const out = v
    .map((x) => ({ label: String((x as DrawStage)?.label ?? "").trim(), pct: Number((x as DrawStage)?.pct) }))
    .filter((x) => x.label && Number.isFinite(x.pct) && x.pct > 0);
  return out.length ? out : null;
}

/** The company-wide default split (Settings → New Construction). */
export function useNcDrawDefault() {
  return useQuery({
    queryKey: ["org_setting", "nc_draw_split_default"],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<DrawStage[]> => {
      const { data } = await supabase.from("org_setting").select("value").eq("key", "nc_draw_split_default").maybeSingle();
      return cleanSplit((data as { value: unknown } | null)?.value) ?? NC_DRAW_SPLIT_FALLBACK;
    },
  });
}

export function useBuilder(custno: string | null | undefined) {
  return useQuery({
    queryKey: ["pegasus_builder", custno],
    enabled: !!custno,
    queryFn: async (): Promise<Builder | null> => {
      const { data, error } = await supabase.from("pegasus_builder").select("*").eq("custno", custno!).maybeSingle();
      if (error) throw error;
      if (!data) return null;
      const b = data as Builder;
      return { ...b, draw_split: cleanSplit(b.draw_split) };
    },
  });
}

export function useSaveBuilder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: Omit<Builder, "updated_at" | "updated_by">) => {
      const who = (await supabase.auth.getUser()).data.user?.email ?? null;
      const { error } = await supabase.from("pegasus_builder").upsert({
        ...v,
        draw_split: v.draw_split && v.draw_split.length ? v.draw_split : null,
        billing_email: (v.billing_email ?? "").trim() || null,
        notes: (v.notes ?? "").trim() || null,
        updated_at: new Date().toISOString(),
        updated_by: who,
      }, { onConflict: "custno" });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pegasus_builder"] }),
  });
}

/** The split that governs a house: its own plan, else the builder's, else the company default. */
export function effectiveDrawPlan(
  job: Pick<Job, "draw_plan"> | null | undefined,
  builder: Builder | null | undefined,
  orgDefault: DrawStage[] | undefined,
): { plan: DrawStage[]; source: "house" | "builder" | "company" } {
  const own = cleanSplit(job?.draw_plan);
  if (own) return { plan: own, source: "house" };
  if (builder?.draw_split?.length) return { plan: builder.draw_split, source: "builder" };
  return { plan: orgDefault ?? NC_DRAW_SPLIT_FALLBACK, source: "company" };
}

export interface DrawInvoice {
  id: string;
  label: string | null;
  invoice_no: string | null;
  total: number | null;
  status: string;
  type: string | null;
  completed_date: string | null;
  created_at: string;
  po_number: string | null;
  recipient_email: string | null;
}
/** Every invoice on the house — draws, plus any legacy bare draw rows. */
export function useJobDraws(jobId: string | null) {
  return useQuery({
    queryKey: ["job_invoices", jobId, "draws"],
    enabled: !!jobId,
    queryFn: async (): Promise<DrawInvoice[]> => {
      const { data, error } = await supabase.from("pegasus_job_invoice")
        .select("id, label, invoice_no, total, status, type, completed_date, created_at, po_number, recipient_email")
        .eq("job_id", jobId!).order("created_at").order("id");
      if (error) throw error;
      return (data ?? []) as DrawInvoice[];
    },
  });
}

export class OverContractError extends Error {
  over: number;
  constructor(over: number) { super("OVER_CONTRACT"); this.over = over; }
}

export function useBillNcDraw() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { jobId: string; label: string; amount: number; pct?: number | null; date?: string | null; allowOver?: boolean }) => {
      const { data, error } = await supabase.rpc("bill_nc_draw", {
        p_job_id: v.jobId, p_label: v.label, p_amount: v.amount,
        p_pct: v.pct ?? null, p_invoice_date: v.date ?? null, p_allow_over: v.allowOver ?? false,
      });
      if (error) {
        if (/OVER_CONTRACT/.test(error.message ?? "")) throw new OverContractError(Number(error.details) || 0);
        throw error;
      }
      return data as { id: string; invoice_no: string; billed: number; contract: number };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["job_invoices"] });
      qc.invalidateQueries({ queryKey: ["job_invoice"] });
      qc.invalidateQueries({ queryKey: ["pegasus_customer_invoices"] });
      qc.invalidateQueries({ queryKey: ["invoices_unified_peg"] });
      qc.invalidateQueries({ queryKey: ["unsent_billable_invoices"] });
    },
  });
}

/** House fields the billing panel edits: its own split and the builder's PO. */
export function useSetJobBilling() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string; draw_plan?: DrawStage[] | null; po_number?: string | null }) => {
      const patch: Record<string, unknown> = { updated_at: new Date().toISOString() };
      if (v.draw_plan !== undefined) patch.draw_plan = v.draw_plan && v.draw_plan.length ? v.draw_plan : null;
      if (v.po_number !== undefined) patch.po_number = (v.po_number ?? "").trim() || null;
      const { error } = await supabase.from("pegasus_job").update(patch).eq("id", v.id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["job"] });
      qc.invalidateQueries({ queryKey: ["jobs"] });
    },
  });
}

/** Close a house: equipment onto the property record, inventory out, locked. */
export function useCloseNcJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (jobId: string) => {
      const { data, error } = await supabase.rpc("close_nc_job", { p_job_id: jobId });
      if (error) throw error;
      return data as { ok: boolean; property_id: string; equipment_posted: number; unbilled: number };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["jobs"] });
      qc.invalidateQueries({ queryKey: ["job"] });
      qc.invalidateQueries({ queryKey: ["job_equipment"] });
      qc.invalidateQueries({ queryKey: ["job_property"] });
      qc.invalidateQueries({ queryKey: ["installed_equipment"] });
    },
  });
}
